import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@stores/authStore';
import type { DashboardStats } from '../types/index';
import {
  BarChart3,
  CheckCircle,
  AlertCircle,
  Clock,
  TrendingUp,
  LogOut,
  Activity,
  PieChart,
  LineChart,
  Calendar,
  Filter,
  Download,
  RefreshCw,
  AlertTriangle,
  Zap,
  Target,
  Users,
  Settings,
} from 'lucide-react';

interface EnhancedStats extends DashboardStats {
  arInvoices: number;
  apInvoices: number;
  ublCompliance: number;
  ftaCompliance: number;
  averageProcessingTime: number;
  peakProcessingTime: number;
  systemUptime: number;
  lastSyncTime: string;
  activeUsers: number;
  failureRate: number;
  retryCount: number;
  averageRetryTime: number;
}

interface TimeSeriesData {
  timestamp: string;
  processed: number;
  failed: number;
  pending: number;
}

interface ComplianceMetrics {
  ubl: number;
  fta: number;
  security: number;
  overall: number;
}

export const EnhancedDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, adapter, clearAuth } = useAuthStore();
  const [stats, setStats] = useState<EnhancedStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<'24h' | '7d' | '30d'>('24h');
  const [timeSeriesData, setTimeSeriesData] = useState<TimeSeriesData[]>([]);
  const [complianceMetrics, setComplianceMetrics] = useState<ComplianceMetrics | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, [timeRange]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      // Mock enhanced stats
      const mockStats: EnhancedStats = {
        totalInvoices: 1250,
        processedInvoices: 1180,
        pendingInvoices: 45,
        errorInvoices: 25,
        successRate: 94.4,
        averageProcessingTime: 2.3,
        arInvoices: 650,
        apInvoices: 600,
        ublCompliance: 98.5,
        ftaCompliance: 97.2,
        peakProcessingTime: 5.8,
        systemUptime: 99.9,
        lastSyncTime: new Date().toISOString(),
        activeUsers: 12,
        failureRate: 2.0,
        retryCount: 45,
        averageRetryTime: 1.5,
      };

      // Mock time series data
      const mockTimeSeries: TimeSeriesData[] = [
        { timestamp: '00:00', processed: 120, failed: 5, pending: 15 },
        { timestamp: '04:00', processed: 145, failed: 8, pending: 12 },
        { timestamp: '08:00', processed: 200, failed: 10, pending: 20 },
        { timestamp: '12:00', processed: 250, failed: 12, pending: 25 },
        { timestamp: '16:00', processed: 280, failed: 15, pending: 30 },
        { timestamp: '20:00', processed: 185, failed: 8, pending: 18 },
      ];

      // Mock compliance metrics
      const mockCompliance: ComplianceMetrics = {
        ubl: 98.5,
        fta: 97.2,
        security: 99.1,
        overall: 98.3,
      };

      setStats(mockStats);
      setTimeSeriesData(mockTimeSeries);
      setComplianceMetrics(mockCompliance);
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboardData();
    setRefreshing(false);
  };

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
  };

  const handleExportReport = () => {
    const report = {
      timestamp: new Date().toISOString(),
      stats,
      timeSeriesData,
      complianceMetrics,
    };
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dashboard-report-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading enhanced dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <img src="/logo.webp" alt="Logo" className="h-55"></img>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Enhanced Dashboard</h1>
                <p className="text-gray-600 mt-1">
                  Welcome back, {user?.name} • Connected to {adapter?.name}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={handleExportReport}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg transition"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Time Range Filter */}
        <div className="mb-6 flex gap-2">
          <button
            onClick={() => setTimeRange('24h')}
            className={`px-4 py-2 rounded-lg font-medium transition ${timeRange === '24h'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
          >
            <Calendar className="w-4 h-4 inline mr-2" />
            Last 24 Hours
          </button>
          <button
            onClick={() => setTimeRange('7d')}
            className={`px-4 py-2 rounded-lg font-medium transition ${timeRange === '7d'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
          >
            <Calendar className="w-4 h-4 inline mr-2" />
            Last 7 Days
          </button>
          <button
            onClick={() => setTimeRange('30d')}
            className={`px-4 py-2 rounded-lg font-medium transition ${timeRange === '30d'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
          >
            <Calendar className="w-4 h-4 inline mr-2" />
            Last 30 Days
          </button>
        </div>

        {/* Key Metrics Grid */}
        {stats && (
          <>
            {/* Primary Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
              {/* Total Invoices */}
              <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm font-medium">Total Invoices</p>
                    <p className="text-3xl font-bold text-gray-900 mt-2">
                      {stats.totalInvoices.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      AR: {stats.arInvoices} | AP: {stats.apInvoices}
                    </p>
                  </div>
                  <BarChart3 className="w-12 h-12 text-blue-100" />
                </div>
              </div>

              {/* Processed */}
              <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm font-medium">Processed</p>
                    <p className="text-3xl font-bold text-green-600 mt-2">
                      {stats.processedInvoices.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      {((stats.processedInvoices / stats.totalInvoices) * 100).toFixed(1)}% of total
                    </p>
                  </div>
                  <CheckCircle className="w-12 h-12 text-green-100" />
                </div>
              </div>

              {/* Pending */}
              <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm font-medium">Pending</p>
                    <p className="text-3xl font-bold text-yellow-600 mt-2">{stats.pendingInvoices}</p>
                    <p className="text-xs text-gray-500 mt-2">
                      {((stats.pendingInvoices / stats.totalInvoices) * 100).toFixed(1)}% of total
                    </p>
                  </div>
                  <Clock className="w-12 h-12 text-yellow-100" />
                </div>
              </div>

              {/* Errors */}
              <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm font-medium">Errors</p>
                    <p className="text-3xl font-bold text-red-600 mt-2">{stats.errorInvoices}</p>
                    <p className="text-xs text-gray-500 mt-2">
                      Failure Rate: {stats.failureRate.toFixed(1)}%
                    </p>
                  </div>
                  <AlertCircle className="w-12 h-12 text-red-100" />
                </div>
              </div>

              {/* Success Rate */}
              <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm font-medium">Success Rate</p>
                    <p className="text-3xl font-bold text-blue-600 mt-2">
                      {stats.successRate.toFixed(1)}%
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      Retries: {stats.retryCount}
                    </p>
                  </div>
                  <TrendingUp className="w-12 h-12 text-blue-100" />
                </div>
              </div>
            </div>

            {/* Advanced Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {/* Processing Time */}
              <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
                <div className="flex items-center gap-3 mb-4">
                  <Zap className="w-5 h-5 text-orange-600" />
                  <h3 className="font-semibold text-gray-900">Processing Time</h3>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Average</span>
                    <span className="font-semibold text-gray-900">
                      {stats.averageProcessingTime.toFixed(2)}s
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Peak</span>
                    <span className="font-semibold text-gray-900">
                      {stats.peakProcessingTime.toFixed(2)}s
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Avg Retry</span>
                    <span className="font-semibold text-gray-900">
                      {stats.averageRetryTime.toFixed(2)}s
                    </span>
                  </div>
                </div>
              </div>

              {/* Compliance Metrics */}
              {complianceMetrics && (
                <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
                  <div className="flex items-center gap-3 mb-4">
                    <Target className="w-5 h-5 text-green-600" />
                    <h3 className="font-semibold text-gray-900">Compliance</h3>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">UBL</span>
                      <span className="font-semibold text-gray-900">
                        {complianceMetrics.ubl.toFixed(1)}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">FTA</span>
                      <span className="font-semibold text-gray-900">
                        {complianceMetrics.fta.toFixed(1)}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Overall</span>
                      <span className="font-semibold text-green-600">
                        {complianceMetrics.overall.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* System Health */}
              <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
                <div className="flex items-center gap-3 mb-4">
                  <Activity className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-gray-900">System Health</h3>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Uptime</span>
                    <span className="font-semibold text-gray-900">
                      {stats.systemUptime.toFixed(2)}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Active Users</span>
                    <span className="font-semibold text-gray-900">{stats.activeUsers}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Last Sync</span>
                    <span className="font-semibold text-gray-900 text-xs">
                      {new Date(stats.lastSyncTime).toLocaleTimeString()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
                <div className="flex items-center gap-3 mb-4">
                  <PieChart className="w-5 h-5 text-purple-600" />
                  <h3 className="font-semibold text-gray-900">Quick Stats</h3>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">UBL Compliance</span>
                    <span className="font-semibold text-gray-900">
                      {stats.ublCompliance.toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">FTA Compliance</span>
                    <span className="font-semibold text-gray-900">
                      {stats.ftaCompliance.toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Retry Count</span>
                    <span className="font-semibold text-gray-900">{stats.retryCount}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Time Series Chart */}
            <div className="bg-white rounded-lg shadow p-6 mb-8">
              <div className="flex items-center gap-3 mb-6">
                <LineChart className="w-5 h-5 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">Processing Trend ({timeRange})</h3>
              </div>
              <div className="h-64 bg-gray-50 rounded-lg p-4">
                {timeSeriesData && timeSeriesData.length > 0 ? (
                  <svg width="100%" height="100%" viewBox="0 0 800 200" className="w-full h-full">
                    {/* Grid lines */}
                    <line x1="40" y1="20" x2="40" y2="180" stroke="#e5e7eb" strokeWidth="1" />
                    <line x1="40" y1="180" x2="780" y2="180" stroke="#e5e7eb" strokeWidth="1" />

                    {/* Y-axis labels */}
                    <text x="10" y="25" fontSize="12" fill="#6b7280">300</text>
                    <text x="10" y="105" fontSize="12" fill="#6b7280">150</text>
                    <text x="10" y="185" fontSize="12" fill="#6b7280">0</text>

                    {/* Grid horizontal lines */}
                    <line x1="40" y1="100" x2="780" y2="100" stroke="#f3f4f6" strokeWidth="1" />

                    {/* Calculate max value for scaling */}
                    {(() => {
                      const maxValue = Math.max(
                        ...timeSeriesData.map(d => Math.max(d.processed || 0, d.failed || 0, d.pending || 0))
                      );
                      const scale = maxValue > 0 ? 160 / maxValue : 1;
                      const xStep = timeSeriesData.length > 1 ? (780 - 40) / (timeSeriesData.length - 1) : 0;

                      // Processed line (blue)
                      const processedPath = timeSeriesData
                        .map((d, i) => {
                          const x = 40 + i * xStep;
                          const y = 180 - (d.processed || 0) * scale;
                          return isFinite(x) && isFinite(y) ? `${x},${y}` : null;
                        })
                        .filter(Boolean)
                        .join(' L ');

                      // Failed line (red)
                      const failedPath = timeSeriesData
                        .map((d, i) => {
                          const x = 40 + i * xStep;
                          const y = 180 - (d.failed || 0) * scale;
                          return isFinite(x) && isFinite(y) ? `${x},${y}` : null;
                        })
                        .filter(Boolean)
                        .join(' L ');

                      // Pending line (yellow)
                      const pendingPath = timeSeriesData
                        .map((d, i) => {
                          const x = 40 + i * xStep;
                          const y = 180 - (d.pending || 0) * scale;
                          return isFinite(x) && isFinite(y) ? `${x},${y}` : null;
                        })
                        .filter(Boolean)
                        .join(' L ');

                      return (
                        <>
                          {/* Processed line */}
                          {processedPath && <polyline points={processedPath} fill="none" stroke="#3b82f6" strokeWidth="2" />}

                          {/* Failed line */}
                          {failedPath && <polyline points={failedPath} fill="none" stroke="#ef4444" strokeWidth="2" />}

                          {/* Pending line */}
                          {pendingPath && <polyline points={pendingPath} fill="none" stroke="#eab308" strokeWidth="2" />}

                          {/* X-axis labels */}
                          {timeSeriesData.map((d, i) => (
                            <text
                              key={i}
                              x={40 + i * xStep}
                              y="200"
                              fontSize="11"
                              fill="#6b7280"
                              textAnchor="middle"
                            >
                              {d.timestamp}
                            </text>
                          ))}
                        </>
                      );
                    })()}
                  </svg>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <LineChart className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                      <p className="text-gray-500">No data available</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Legend */}
              <div className="flex gap-6 mt-4 justify-center">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                  <span className="text-sm text-gray-600">Processed</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-red-600 rounded-full"></div>
                  <span className="text-sm text-gray-600">Failed</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">Pending</span>
                </div>
              </div>
            </div>

            {/* Alerts and Warnings */}
            {stats.errorInvoices > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-yellow-900">Active Alerts</h3>
                    <p className="text-sm text-yellow-800 mt-1">
                      {stats.errorInvoices} invoices have processing errors. Review logs for details.
                    </p>
                    <button
                      onClick={() => navigate('/logs')}
                      className="mt-3 text-sm font-semibold text-yellow-700 hover:text-yellow-900 underline"
                    >
                      View Logs →
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* AR Invoices */}
          <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">AR Invoices</h3>
            <p className="text-gray-600 text-sm mb-4">
              Manage outgoing invoices and track their status
            </p>
            <button
              onClick={() => navigate('/ar-invoices')}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition"
            >
              View AR Invoices
            </button>
          </div>

          {/* AP Invoices */}
          <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">AP Invoices</h3>
            <p className="text-gray-600 text-sm mb-4">
              Process incoming invoices and match with POs
            </p>
            <button
              onClick={() => navigate('/ap-invoices')}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition"
            >
              View AP Invoices
            </button>
          </div>

          {/* System Logs */}
          <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">System Logs</h3>
            <p className="text-gray-600 text-sm mb-4">
              Monitor system activity and troubleshoot issues
            </p>
            <button
              onClick={() => navigate('/logs')}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition"
            >
              View Logs
            </button>
          </div>

          {/* Settings */}
          <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Settings</h3>
            <p className="text-gray-600 text-sm mb-4">
              Configure system connections and settings
            </p>
            <button
              onClick={() => navigate('/settings')}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition"
            >
              Configure
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
